import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorHandler';

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.fetchNotifications(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch notifications'));
    }
  }
);

export const markRead = createAsyncThunk(
  'notifications/markRead',
  async (id, { rejectWithValue }) => {
    try {
      await notificationAPI.markRead(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to mark as read'));
    }
  }
);

export const markAllRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.markAllRead();
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to mark all as read'));
    }
  }
);

export const markAllReadByCategory = createAsyncThunk(
  'notifications/markAllReadByCategory',
  async (category, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.markAllReadByCategory(category);
      return { category, ...response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to mark all as read'));
    }
  }
);

export const acknowledgeNotification = createAsyncThunk(
  'notifications/acknowledge',
  async (id, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.acknowledge(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to acknowledge'));
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id, { rejectWithValue }) => {
    try {
      await notificationAPI.deleteNotification(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to delete'));
    }
  }
);

export const fetchPreferences = createAsyncThunk(
  'notifications/preferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.fetchPreferences();
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch preferences'));
    }
  }
);

export const updatePreferences = createAsyncThunk(
  'notifications/updatePreferences',
  async (data, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.updatePreferences(data);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update preferences'));
    }
  }
);

export const fetchCategoryPreferences = createAsyncThunk(
  'notifications/categoryPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.fetchCategoryPreferences();
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch category preferences'));
    }
  }
);

export const updateCategoryPreference = createAsyncThunk(
  'notifications/updateCategoryPreference',
  async (data, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.updateCategoryPreference(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update category preference'));
    }
  }
);

export const resetCategoryPreferences = createAsyncThunk(
  'notifications/resetCategoryPreferences',
  async (_, { rejectWithValue }) => {
    try {
      await notificationAPI.resetCategoryPreferences();
      const response = await notificationAPI.fetchCategoryPreferences();
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to reset'));
    }
  }
);

export const fetchNotificationStats = createAsyncThunk(
  'notifications/stats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch stats'));
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/unreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getUnreadCount();
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch unread count'));
    }
  }
);

const initialState = {
  notifications: [],
  totalNotifications: 0,
  unreadCount: 0,
  preferences: null,
  categoryPreferences: [],
  stats: null,
  loading: false,
  error: null,
  hasMore: true,
  filters: {
    category: null,
    priority: null,
    unreadOnly: false,
    search: '',
  },
  connectionStatus: 'disconnected',
  soundEnabled: true,
  soundVolume: 0.3,
  _pollTimer: null,
  _visibilityHandler: null,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationError: (state) => {
      state.error = null;
    },
    addRealtimeNotification: (state, action) => {
      const notif = action.payload;
      const exists = state.notifications.find((n) => n.id === notif.id);
      if (!exists) {
        state.notifications.unshift(notif);
        if (!notif.is_read && !notif.read) state.unreadCount += 1;
      }
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { category: null, priority: null, unreadOnly: false, search: '' };
    },
    updateNotificationInList: (state, action) => {
      const idx = state.notifications.findIndex((n) => n.id === action.payload.id);
      if (idx !== -1) {
        state.notifications[idx] = { ...state.notifications[idx], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const data = payload.data || payload;
        if (payload.skip && payload.skip > 0) {
          state.notifications = [...state.notifications, ...data];
        } else {
          state.notifications = data;
        }
        state.totalNotifications = payload.total || data.length;
        state.hasMore = data.length >= (payload.limit || 50);
        state.unreadCount = data.filter((n) => !n.read && !n.is_read).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markRead.fulfilled, (state, action) => {
        const id = action.payload;
        state.notifications = state.notifications.map((n) =>
          n.id === id ? { ...n, read: true, is_read: true } : n
        );
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      .addCase(markAllRead.fulfilled, (state) => {
        state.notifications = state.notifications.map((n) => ({ ...n, read: true, is_read: true }));
        state.unreadCount = 0;
      })
      .addCase(markAllReadByCategory.fulfilled, (state, action) => {
        const { category } = action.meta.arg ? action.meta.arg : action.payload;
        state.notifications = state.notifications.map((n) =>
          (n.category === category) ? { ...n, read: true, is_read: true } : n
        );
        state.unreadCount = state.notifications.filter((n) => !n.read && !n.is_read).length;
      })
      .addCase(acknowledgeNotification.fulfilled, (state, action) => {
        const notif = action.payload;
        state.notifications = state.notifications.map((n) =>
          n.id === notif.id ? { ...n, ...notif } : n
        );
        if (!notif.read && !notif.is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const id = action.payload;
        const wasUnread = state.notifications.find((n) => n.id === id && (!n.read && !n.is_read));
        state.notifications = state.notifications.filter((n) => n.id !== id);
        if (wasUnread) state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        if (action.payload?.sound_enabled !== undefined) {
          state.soundEnabled = action.payload.sound_enabled;
        }
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        if (action.payload?.sound_enabled !== undefined) {
          state.soundEnabled = action.payload.sound_enabled;
        }
      })
      .addCase(fetchCategoryPreferences.fulfilled, (state, action) => {
        state.categoryPreferences = action.payload;
      })
      .addCase(updateCategoryPreference.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.categoryPreferences.findIndex((p) => p.category === updated.category);
        if (idx !== -1) {
          state.categoryPreferences[idx] = updated;
        } else {
          state.categoryPreferences.push(updated);
        }
      })
      .addCase(resetCategoryPreferences.fulfilled, (state, action) => {
        state.categoryPreferences = action.payload;
      })
      .addCase(fetchNotificationStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.unread_count ?? 0;
      });
  },
});

export const {
  clearNotificationError,
  addRealtimeNotification,
  setUnreadCount,
  setConnectionStatus,
  setFilters,
  clearFilters,
  updateNotificationInList,
} = notificationSlice.actions;
export default notificationSlice.reducer;
